import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useMe, useCan } from "@/lib/authz";
import { Plus, Trophy, Paperclip } from "@phosphor-icons/react";
import CreateOfferModal from "@/components/CreateOfferModal";
import ConfirmInterviewModal from "@/components/ConfirmInterviewModal";
import ScopeFilterBar, { scopeToQuery } from "@/components/ScopeFilterBar";

const STATUSES = ["Scheduled", "Confirmed", "Completed", "Selected", "Rejected", "Cancelled", "No Show"];
const CLS = {
  Scheduled: "bg-sky-50 text-sky-700 border border-sky-200",
  Confirmed: "bg-violet-50 text-violet-700 border border-violet-200",
  Completed: "bg-slate-50 text-slate-700 border border-slate-200",
  Selected: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Rejected: "bg-red-50 text-red-700 border border-red-200",
  Cancelled: "bg-amber-50 text-amber-700 border border-amber-200",
  "No Show": "bg-slate-50 text-slate-500 border border-slate-200",
};

export default function Interviews() {
  const { user } = useAuth();
  const { hasPerm, canSeeOwner } = useMe();
  const role = user?.role;
  // Phase A/B/C/D — dual check: user must have candidate.edit AND the interview's
  // owner (employee_id) must be within their visibility. Group Leads/Managers still
  // supervise view-only per the operational-IC rule.
  const canCreate = useCan("candidate.edit") && ["admin", "super_admin", "team_lead", "senior_executive", "executive", "sde"].includes(role);
  const canEditRow = (iv) => {
    if (role === "admin" || role === "super_admin") return true;
    if (role === "sdl" || role === "team_lead") return false;   // GL/Manager supervise, no edit
    // Executive / TL / SDE — check permission AND ownership via visible_owner_ids.
    return hasPerm("candidate.edit") && canSeeOwner(iv.employee_id);
  };
  const [items, setItems] = useState([]);
  const [cands, setCands] = useState([]);
  const [open, setOpen] = useState(false);
  const [candFilter, setCandFilter] = useState("");
  const [filters, setFilters] = useState({});
  const [confirmFor, setConfirmFor] = useState(null);   // Interview Confirmation Proof modal target
  const [offerModal, setOfferModal] = useState(null);        // { candidateId, candidateName, offerId, interviewId, companyDefault, roleDefault }
  const [refreshOffers, setRefreshOffers] = useState(0);
  const [form, setForm] = useState({
    candidate_id: "", company: "", job_title: "",
    interview_date: new Date().toISOString().slice(0, 10),
    interview_time: "10:00",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
    interviewer_or_contact: "",
    round: "R1", recruiter: "", interview_type: "Video",
    status: "Scheduled", notes: "",
    scheduling_proof_document_id: "",
  });
  const [proofFile, setProofFile] = useState(null);
  const [proofName, setProofName] = useState("");
  const [uploadingProof, setUploadingProof] = useState(false);

  const load = useCallback(() => api.get("/interviews").then((r) => setItems(r.data)), []);
  useEffect(() => {
    load();
    api.get("/candidates").then((r) => setCands(r.data));
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.scheduling_proof_document_id && !proofFile) {
      toast.error("Scheduling Proof is required — attach a client calendar invite, email confirmation, or ATS confirmation.");
      return;
    }
    try {
      let proofId = form.scheduling_proof_document_id;
      // Upload the proof if the user attached a fresh file.
      if (proofFile && !proofId) {
        setUploadingProof(true);
        const fd = new FormData();
        fd.append("file", proofFile);
        fd.append("candidate_id", form.candidate_id);
        fd.append("doc_type", "scheduling_proof");
        const up = await api.post("/documents/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        proofId = up.data?.id || up.data?.document_id;
        setUploadingProof(false);
      }
      const payload = { ...form, scheduling_proof_document_id: proofId };
      await api.post("/interviews", payload);
      toast.success("Interview scheduled");
      setOpen(false);
      setProofFile(null);
      setProofName("");
      setForm((f) => ({ ...f, candidate_id: "", company: "", job_title: "",
                        interviewer_or_contact: "", notes: "",
                        scheduling_proof_document_id: "" }));
      load();
    } catch (ex) {
      setUploadingProof(false);
      toast.error(formatApiError(ex.response?.data?.detail));
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const r = await api.put(`/interviews/${id}`, { status });
      load();
      if (status === "Selected") {
        const iv = r.data;
        const cand = cands.find((c) => c.id === iv.candidate_id);
        toast.success("Interview marked Selected — please complete the Offer");
        setOfferModal({
          candidateId: iv.candidate_id,
          candidateName: cand?.full_name || "Candidate",
          offerId: r.data?._drafted_offer_id || null,
          interviewId: id,
          companyDefault: iv.company,
          roleDefault: iv.job_title,
        });
      } else {
        toast.success("Updated");
      }
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const cols = STATUSES.slice(0, 6);
  const grouped = Object.fromEntries(cols.map((s) => [s, items.filter((i) => i.status === s)]));

  return (
    <div className="space-y-5" data-testid="interviews-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Interviews &amp; Offers</h2>
          <p className="text-sm text-slate-500">Pipeline from scheduled to selected. Marking an interview <b>Selected</b> auto-creates an Offer draft. Confirming an interview requires the mandatory Interview Confirmation Proof.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="add-interview-button"
                disabled={!canCreate}
                title={!canCreate ? "View-only for your role" : ""}
                className={`text-sm px-4 py-2 rounded-md flex items-center gap-2 ${canCreate ? "bg-sky-600 hover:bg-sky-700 text-white" : "bg-slate-100 text-slate-400 cursor-not-allowed"}`}>
          <Plus size={16} /> Schedule Interview
        </button>
      </div>

      <ScopeFilterBar value={filters} onChange={setFilters}
                      candidates={cands}
                      showStatus statusOptions={STATUSES} statusLabel="Interview Status"
                      testid="interviews-scope" />

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {cols.map((s) => (
          <div key={s} className="bg-slate-50 border border-slate-200 rounded-lg p-3 min-h-[200px]">
            <div className="flex items-center justify-between mb-3">
              <span className={`text-xs px-2 py-0.5 rounded-full ${CLS[s]}`}>{s}</span>
              <span className="text-xs text-slate-500">{grouped[s].length}</span>
            </div>
            <div className="space-y-2">
              {grouped[s].map((i) => {
                const c = cands.find((x) => x.id === i.candidate_id);
                return (
                  <div key={i.id} className="bg-white border border-slate-200 rounded-md p-3 text-sm hover:border-slate-300">
                    <div className="font-medium">{i.company}</div>
                    <div className="text-xs text-slate-500">{i.job_title}</div>
                    <div className="text-xs text-slate-500 mt-1">{c?.full_name || "—"} · {i.interview_date} · {i.round}</div>
                    {canEditRow(i) ? (
                      <select value={i.status} onChange={(e) => updateStatus(i.id, e.target.value)}
                              className="mt-2 w-full border border-slate-200 rounded-md px-2 py-1 text-xs">
                        {STATUSES.map((st) => <option key={st}>{st}</option>)}
                      </select>
                    ) : (
                      <div className="mt-2 text-xs text-slate-500 border border-slate-200 rounded-md px-2 py-1 bg-slate-50">
                        {i.status} <span className="text-slate-400">(view-only)</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Combined Offers panel — since Offers now live under Interviews */}
      <InterviewOffersPanel candidates={cands} filter={candFilter} onChange={load} refreshKey={refreshOffers}
                            onEdit={(o) => setOfferModal({
                              candidateId: o.candidate_id,
                              candidateName: cands.find((c) => c.id === o.candidate_id)?.full_name || "Candidate",
                              offerId: o.id, interviewId: o.interview_id,
                              companyDefault: o.company, roleDefault: o.job_title,
                            })} />

      {confirmFor && (
        <ConfirmInterviewModal
          interview={confirmFor}
          onClose={() => setConfirmFor(null)}
          onConfirmed={() => { setConfirmFor(null); load(); }}
        />
      )}

      {offerModal && (
        <CreateOfferModal open onClose={() => setOfferModal(null)}
                          candidateId={offerModal.candidateId} candidateName={offerModal.candidateName}
                          offerId={offerModal.offerId} interviewId={offerModal.interviewId}
                          companyDefault={offerModal.companyDefault} roleDefault={offerModal.roleDefault}
                          onSaved={() => setRefreshOffers((n) => n + 1)} />
      )}

      {open && canCreate && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm grid place-items-center p-4 z-50">
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-6 w-full max-w-lg">
            <div className="space-y-3">
              <select required value={form.candidate_id} onChange={(e) => setForm((f) => ({ ...f, candidate_id: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                <option value="">Select candidate…</option>
                {cands.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="Client / Company" data-testid="iv-company"
                       value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                       className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
                <input required placeholder="Job / Requirement" data-testid="iv-job-title"
                       value={form.job_title} onChange={(e) => setForm((f) => ({ ...f, job_title: e.target.value }))}
                       className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
                <input required type="date" data-testid="iv-date"
                       value={form.interview_date} onChange={(e) => setForm((f) => ({ ...f, interview_date: e.target.value }))}
                       className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
                <input required type="time" data-testid="iv-time"
                       value={form.interview_time} onChange={(e) => setForm((f) => ({ ...f, interview_time: e.target.value }))}
                       className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
                <input required placeholder="Timezone (e.g. America/New_York)" data-testid="iv-timezone"
                       value={form.timezone} onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
                       className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
                <input required placeholder="Interviewer / Client Contact" data-testid="iv-interviewer"
                       value={form.interviewer_or_contact}
                       onChange={(e) => setForm((f) => ({ ...f, interviewer_or_contact: e.target.value }))}
                       className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
                <input placeholder="Round (R1, R2…)" data-testid="iv-round"
                       value={form.round} onChange={(e) => setForm((f) => ({ ...f, round: e.target.value }))}
                       className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
                <select value={form.interview_type} data-testid="iv-type"
                        onChange={(e) => setForm((f) => ({ ...f, interview_type: e.target.value }))}
                        className="border border-slate-200 rounded-md px-3 py-2 text-sm">
                  <option>Video</option><option>Phone</option><option>Onsite</option><option>Technical</option>
                </select>
                <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                        className="border border-slate-200 rounded-md px-3 py-2 text-sm">
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              {/* Scheduling Proof (required) */}
              <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                  <Paperclip size={12} /> Scheduling Proof <span className="text-red-500">*</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 mb-2">
                  Attach the client calendar invite, client email confirmation, ATS confirmation,
                  or another approved client-originated record. One proof per interview.
                </div>
                <input type="file" data-testid="iv-scheduling-proof"
                       accept=".pdf,.png,.jpg,.jpeg,.eml,.msg,.ics"
                       onChange={(e) => {
                         const f = e.target.files?.[0];
                         setProofFile(f || null);
                         setProofName(f?.name || "");
                       }}
                       className="text-xs" />
                {proofName && (
                  <div className="text-[11px] text-slate-600 mt-1" data-testid="iv-proof-selected">
                    Attached: {proofName}
                  </div>
                )}
              </div>
              <textarea rows={2} placeholder="Notes" value={form.notes}
                        onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-md border border-slate-200">Cancel</button>
              <button type="submit" disabled={uploadingProof} data-testid="iv-submit"
                      className="px-4 py-2 text-sm rounded-md bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-50">
                {uploadingProof ? "Uploading proof…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const OFFER_STATUS_CLS = {
  "Draft": "bg-slate-50 text-slate-700 border-slate-200",
  "Awaiting Offer Letter": "bg-amber-50 text-amber-700 border-amber-200",
  "Submitted": "bg-sky-50 text-sky-700 border-sky-200",
  "Pending SDL Approval": "bg-violet-50 text-violet-700 border-violet-200",
  "Pending HR Verification": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Approved": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Accepted": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Rejected": "bg-red-50 text-red-700 border-red-200",
};

function InterviewOffersPanel({ candidates, filter, onEdit, refreshKey }) {
  const [offers, setOffers] = useState([]);

  const load = useCallback(() => api.get("/offers").then((r) => setOffers(r.data)), []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const shown = filter ? offers.filter((o) => o.candidate_id === filter) : offers;
  const nameOf = (cid) => candidates.find((c) => c.id === cid)?.full_name || "—";

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="offers-panel">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-1.5"><Trophy size={14} /> Offers</h3>
        <span className="text-xs text-slate-500">{shown.length} offer(s){filter && " · candidate filter active"}</span>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-widest">
          <tr>
            <th className="text-left px-4 py-2">Offer Date</th>
            <th className="text-left px-4 py-2">Candidate</th>
            <th className="text-left px-4 py-2">Company / Role</th>
            <th className="text-left px-4 py-2">Salary</th>
            <th className="text-left px-4 py-2">Status</th>
            <th className="text-left px-4 py-2">Offer Letter</th>
            <th className="text-right px-4 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {shown.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-8">No offers.</td></tr>}
          {shown.map((o) => {
            const needsAction = ["Awaiting Offer Letter", "Draft"].includes(o.status);
            return (
              <tr key={o.id} className={`border-t border-slate-100 hover:bg-slate-50 ${needsAction ? "bg-amber-50/30" : ""}`} data-testid={`offer-row-${o.id}`}>
                <td className="px-4 py-2 text-xs text-slate-500">{o.offer_date}</td>
                <td className="px-4 py-2">{nameOf(o.candidate_id)}</td>
                <td className="px-4 py-2"><b>{o.company}</b><div className="text-xs text-slate-500">{o.job_title}</div></td>
                <td className="px-4 py-2 text-xs">{o.salary || "—"}</td>
                <td className="px-4 py-2">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${OFFER_STATUS_CLS[o.status] || OFFER_STATUS_CLS.Draft}`} data-testid={`offer-status-badge-${o.id}`}>{o.status}</span>
                  {o.auto_drafted && needsAction && <div className="text-[10px] text-amber-700 mt-0.5">auto-drafted from interview</div>}
                </td>
                <td className="px-4 py-2">
                  {o.offer_letter_document_id ? (
                    <span className="text-xs text-emerald-700 inline-flex items-center gap-1"><Paperclip size={11} /> attached</span>
                  ) : (
                    <span className="text-xs text-amber-700 inline-flex items-center gap-1"><Paperclip size={11} /> missing</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => onEdit?.(o)} data-testid={`offer-open-${o.id}`}
                          className={`text-xs px-2.5 py-1 rounded-md ${needsAction ? "bg-sky-600 hover:bg-sky-700 text-white" : "border border-slate-200 hover:bg-slate-100"}`}>
                    {needsAction ? "Complete Offer" : "Open"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

